import { Marker } from './marker'

// NOTE: if we don't have a `get` option, the result can always potentially be
// `undefined`
export function lookup<TKey extends string, TIn, TOut, TStored>(
  config: Required.Semi<
    LookupConfig.WithoutMarker<TKey, TIn, TOut, TStored>,
    'get' | 'set' | 'defaultKey'
  >,
): Required.Semi<
  LookupConfig<TKey, TIn, TOut, TStored>,
  'get' | 'set' | 'defaultKey'
>
export function lookup<TKey extends string, TIn, TOut>(
  config: Required.Semi<
    LookupConfig.WithoutMarker<TKey, TIn, TOut>,
    'set' | 'defaultKey'
  >,
): Required.Semi<
  LookupConfig<TKey, TIn, TOut | undefined>,
  'set' | 'defaultKey'
>
export function lookup<TKey extends string, TOut, TStored>(
  config: Required.Semi<
    LookupConfig.WithoutMarker<TKey, TStored | undefined, TOut, TStored>,
    'get' | 'defaultKey'
  >,
): Required.Semi<
  LookupConfig<TKey, TStored | undefined, TOut, TStored>,
  'get' | 'defaultKey'
>
export function lookup<TKey extends string, TVal>(
  config: Required.Semi<
    LookupConfig.WithoutMarker<TKey, TVal | undefined>,
    'defaultKey'
  >,
): Required.Semi<LookupConfig<TKey, TVal | undefined>, 'defaultKey'>

export function lookup<TKey extends string, TIn, TOut, TStored>(
  config: Required.Semi<
    LookupConfig.WithoutMarker<TKey, TIn, TOut, TStored>,
    'get' | 'set'
  >,
): Required.Semi<LookupConfig<TKey, TIn, TOut, TStored>, 'get' | 'set'>
export function lookup<TKey extends string, TIn, TOut>(
  config: Required.Semi<LookupConfig.WithoutMarker<TKey, TIn, TOut>, 'set'>,
): Required.Semi<LookupConfig<TKey, TIn, TOut | undefined>, 'set'>
export function lookup<TKey extends string, TOut, TStored>(
  config: Required.Semi<
    LookupConfig.WithoutMarker<TKey, TStored | undefined, TOut, TStored>,
    'get'
  >,
): Required.Semi<LookupConfig<TKey, TStored | undefined, TOut, TStored>, 'get'>
export function lookup<TKey extends string, TVal>(
  config?: LookupConfig.WithoutMarker<TKey, TVal | undefined>,
): LookupConfig<TKey, TVal | undefined>

export function lookup<TKey extends string, TIn, TOut, TStored>(
  config: LookupConfig.WithoutMarker<TKey, TIn, TOut, TStored> = {},
): LookupConfig<TKey, TIn, TOut, TStored> {
  return Marker.mark(config, Marker.Type.Lookup)
}

export interface LookupConfig<
  TKey extends string,
  TIn,
  TOut = TIn,
  TStored = TOut,
> extends Marker<Marker.Type.Lookup> {
  readonly defaultKey?: TKey
  readonly get?: (key: TKey, stored: TStored | undefined) => TOut
  readonly set?: (
    key: TKey,
    input: TIn,
    current: TStored | undefined,
  ) => TStored | undefined
}
export namespace LookupConfig {
  export type Constraint<
    TKey extends string = any,
    TIn = any,
    TOut = any,
    TStored = any,
  > = LookupConfig<TKey, TIn, TOut, TStored>

  export type Default<
    TKey extends string = string,
    TIn = unknown,
    TOut = unknown,
    TStored = unknown,
  > = LookupConfig<TKey, TIn, TOut, TStored>

  export type WithoutMarker<
    TKey extends string,
    TIn,
    TOut = TIn,
    TStored = TOut,
  > = Difference<
    LookupConfig<TKey, TIn, TOut, TStored>,
    Marker<Marker.Type.Lookup>
  >
}
